import { useMemo, useState } from 'react'
import { Loader2, X, Stethoscope, Mail, Phone, AlertTriangle, CheckCircle2, UserPlus } from 'lucide-react'
import { postJson, invalidateCache } from '../../lib/api.js'
import SearchableSelect from '../SearchableSelect.jsx'
import { buildCode } from '../../lib/dentist.js'

const INPUT = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 disabled:bg-slate-50 disabled:text-slate-400'

// Free text, like the drawer's คำนำหน้า — the two below cover ~every record but
// the column is not an enum and legacy rows carry other spellings.
const PREFIX_HINTS = ['ทพ.', 'ทพญ.']

const EMPTY = {
  prefix: '', nickname: '', name: '', surname: '', name_eng: '',
  email: '', phone: '', license: '',
}

/**
 * เพิ่มทันตแพทย์ — Manager › ทันตแพทย์.
 *
 * ONE POST creates the Doctor record AND its doctor-portal login, both DORMANT,
 * and mails the invite. What that means on screen matters more than usual:
 *
 *  - the new doctor does NOT appear in the default ใช้งาน list, and is not
 *    offerable on the booking calendar, until they open the mail and set a
 *    password. So this dialog does not close straight back to a list the doctor
 *    is missing from — it confirms what happened and hands the page a signal to
 *    widen its filter.
 *  - there is no resend button, exactly as in the employee editor: the portal's
 *    ลืมรหัสผ่าน mints a fresh link to the same page and activates on the same
 *    terms, so a second clinic-side path would be attack surface for nothing.
 *
 * Only the identity + contact fields are collected. Banking, idCard, address,
 * เคสที่ไม่รับ and documents are drawer work with their own role rules, and none
 * of them need to exist before the invite goes out.
 */
export default function DentistCreateDialog({ specOptions, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY)
  const [specs, setSpecs] = useState(() => new Set())
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  // The created record — set once the POST lands. Its presence is what swaps the
  // form for the result panel AND what stops a second submit from creating a
  // duplicate doctor (same reasoning as createdId in EmployeeEditor).
  const [created, setCreated] = useState(null)
  const [claimNote, setClaimNote] = useState('')

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }))
  const code = useMemo(() => buildCode({ nickname: form.nickname, name: form.name }), [form.nickname, form.name])

  async function save() {
    const email = form.email.trim().toLowerCase()
    const name = form.name.trim()
    if (!name) { setErr('กรอกชื่อก่อน'); return }
    if (!email) { setErr('กรอกอีเมล — อีเมลคือบัญชีเข้าระบบของหมอ'); return }
    // Deliberately loose: the address is verified for real by whether the invite
    // arrives, and Xano lower-cases + validates it again on the way in.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setErr('รูปแบบอีเมลไม่ถูกต้อง'); return }

    setSaving(true); setErr('')
    try {
      const rec = await postJson('/api/doctors', {
        prefix: form.prefix.trim(),
        nickname: form.nickname.trim(),
        name,
        surname: form.surname.trim(),
        name_eng: form.name_eng.trim(),
        email,
        phone: form.phone.trim(),
        license: form.license.trim(),
        spec: [...specs],
        code,
      })
      // Every /api/doctors cache key holds a list this doctor now belongs to —
      // the booking calendar and the revenue report read the same fetch.
      invalidateCache('/api/doctors')
      setCreated(rec)
      if (rec?._claim_sent === false) {
        const why = String(rec?._claim_error_th || '').trim()
        setClaimNote(
          `สร้างทันตแพทย์เรียบร้อย แต่ส่งอีเมลยืนยันไม่สำเร็จ${why ? ` (${why})` : ''}` +
          ' — บัญชียังปิดใช้งานอยู่ แจ้งคุณหมอให้กด "ลืมรหัสผ่าน" ที่หน้าเข้าสู่ระบบ Doctor Portal' +
          ' แล้วกรอกอีเมลนี้เพื่อตั้งรหัสผ่านและเปิดใช้งานบัญชีด้วยตนเอง',
        )
      }
    } catch (e) {
      // The proxy answers a refused create with a Thai `{error}` (duplicate email,
      // missing role). api.js hands it over parsed on err.body — prefer it over
      // err.message, which is the raw "HTTP 400: {…}" envelope.
      setErr(e?.body?.error || String(e.message || e))
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      {/* No close while saving: the create is in flight, and closing would hide its
          result (and the claim-email note) with the doctor already written. */}
      <div className="absolute inset-0 bg-slate-900/40" onClick={() => { if (!saving) onClose() }} />
      <div className="relative w-full max-w-[560px] bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-[16px] font-semibold text-slate-900">เพิ่มทันตแพทย์</h2>
          <button onClick={onClose} disabled={saving} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 disabled:opacity-40"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {created ? (
            <Result rec={created} claimNote={claimNote} />
          ) : (
            <>
              {/* What this button actually does, before it is pressed. The dormant
                  account is the surprising half and it is stated first. */}
              <div className="rounded-xl bg-brand-50 ring-1 ring-brand-100 px-3.5 py-3 text-[12.5px] text-brand-900 leading-relaxed">
                ระบบจะสร้าง <strong>ข้อมูลทันตแพทย์</strong> พร้อม <strong>บัญชีเข้าใช้งาน Doctor Portal</strong> ให้พร้อมกัน
                โดย<strong>ยังปิดใช้งานทั้งคู่</strong> แล้วส่งอีเมลยืนยันไปยังคุณหมอ —
                บัญชีจะเปิดใช้งานและขึ้นในรายชื่อ “ใช้งาน” ก็ต่อเมื่อคุณหมอกดลิงก์ในอีเมลและตั้งรหัสผ่านแล้วเท่านั้น
              </div>

              <Section icon={Stethoscope} title="ข้อมูลทั่วไป">
                <Row2>
                  <Field label="คำนำหน้า" hint={PREFIX_HINTS.join(' / ')}>
                    <input className={INPUT} value={form.prefix} onChange={set('prefix')} placeholder="ทพ." />
                  </Field>
                  <Field label="ชื่อเล่น"><input className={INPUT} value={form.nickname} onChange={set('nickname')} /></Field>
                </Row2>
                <Row2>
                  <Field label="ชื่อ" hint="จำเป็น"><input className={INPUT} value={form.name} onChange={set('name')} /></Field>
                  <Field label="นามสกุล"><input className={INPUT} value={form.surname} onChange={set('surname')} /></Field>
                </Row2>
                <Field label="ชื่ออังกฤษ"><input className={INPUT} value={form.name_eng} onChange={set('name_eng')} /></Field>
                {/* Derived, never typed — same rule as the drawer. Shown so the
                    handle every other screen displays is visible before saving. */}
                <Field label="ชื่อที่แสดง (code)" hint="สร้างจาก ชื่อเล่น + ชื่อ">
                  <div className="rounded-lg bg-slate-50 px-3 py-1.5 text-[13.5px] text-slate-600">{code || '—'}</div>
                </Field>
              </Section>

              <Section icon={Phone} title="ติดต่อ">
                <Field label="เบอร์โทร"><input className={INPUT} value={form.phone} onChange={set('phone')} /></Field>
              </Section>

              <Section icon={Mail} title="บัญชีเข้าระบบ">
                <Field label="อีเมล" hint="จำเป็น · ใช้เป็นบัญชีเข้า Doctor Portal">
                  <input className={INPUT} value={form.email} onChange={set('email')} type="email" autoComplete="off" placeholder="doctor@example.com" />
                </Field>
                <p className="text-[11.5px] text-slate-400 leading-relaxed">
                  อีเมลนี้คือชื่อผู้ใช้ของคุณหมอ และเป็นที่อยู่ที่ลิงก์ยืนยันจะถูกส่งไป —
                  ต้องเป็นอีเมลที่คุณหมอเข้าถึงได้จริง และต้องไม่ซ้ำกับบัญชีที่มีอยู่แล้วในระบบ
                </p>
              </Section>

              <Section icon={Stethoscope} title="วิชาชีพ">
                <Field label="เลขที่ใบประกอบ"><input className={INPUT} value={form.license} onChange={set('license')} /></Field>
                <Field label="ความเชี่ยวชาญ (spec)">
                  <SearchableSelect
                    multiple
                    options={specOptions}
                    values={specs}
                    onChange={setSpecs}
                    placeholder="— เลือกความเชี่ยวชาญ —"
                    searchPlaceholder="ค้นหาความเชี่ยวชาญ…"
                  />
                </Field>
              </Section>

              <p className="text-[11.5px] text-slate-400 leading-relaxed">
                ข้อมูลบัญชีธนาคาร บัตรประชาชน ที่อยู่ เคสที่ไม่รับ และเอกสาร แก้ไขได้ในหน้ารายละเอียดของหมอหลังสร้างเสร็จ
              </p>
            </>
          )}
        </div>

        <div className="border-t border-slate-100 px-6 py-3.5 flex items-center justify-between gap-3">
          <span className="text-[12.5px] text-rose-500 truncate">{err}</span>
          <div className="flex items-center gap-2 shrink-0">
            {created ? (
              <button
                onClick={() => onCreated?.(created)}
                className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700"
              >
                เสร็จสิ้น
              </button>
            ) : (
              <>
                <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40">ยกเลิก</button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} สร้างและส่งอีเมลยืนยัน
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/* The panel that replaces the form once the doctor exists. It is not decoration:
   the list behind this dialog filters to ใช้งาน by default, so without it the
   dialog would close onto a list the new doctor is absent from. */
function Result({ rec, claimNote }) {
  const failed = Boolean(claimNote)
  return (
    <div className="space-y-4">
      <div className={`rounded-xl px-3.5 py-3 flex items-start gap-2.5 ${failed ? 'bg-amber-50 ring-1 ring-amber-200' : 'bg-emerald-50 ring-1 ring-emerald-200'}`}>
        {failed
          ? <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          : <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />}
        <div className={`text-[12.5px] leading-relaxed ${failed ? 'text-amber-900' : 'text-emerald-900'}`}>
          {failed ? claimNote : (
            <>
              สร้างทันตแพทย์ <strong>{rec?.code || rec?.name || ''}</strong> เรียบร้อย
              และส่งอีเมลยืนยันไปที่ <strong>{rec?.email}</strong> แล้ว
            </>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-[13px]">
        <Line k="สถานะตอนนี้">
          <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[11.5px] font-medium ring-1 ring-slate-200">
            ปิดใช้งาน — รอคุณหมอยืนยัน
          </span>
        </Line>
        <Line k="อีเมล"><span className="text-slate-700">{rec?.email || '—'}</span></Line>
        <Line k="เข้าใช้งานที่"><span className="text-slate-700">Doctor Portal</span></Line>
      </div>

      <p className="text-[11.5px] text-slate-400 leading-relaxed">
        คุณหมอจะยังไม่ขึ้นในรายชื่อ “ใช้งาน” และยังนัดหมายไม่ได้ จนกว่าจะกดลิงก์ในอีเมลและตั้งรหัสผ่าน
        {' '}— ระหว่างนี้ดูได้ที่ตัวกรอง “ปิดใช้งาน”. หากอีเมลหมดอายุ (5 นาที) ให้คุณหมอกด “ลืมรหัสผ่าน”
        {' '}ที่หน้าเข้าสู่ระบบ Doctor Portal เพื่อรับลิงก์ใหม่ได้เอง
      </p>
    </div>
  )
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500">
        <Icon size={14} className="text-slate-400" /> {title}
      </div>
      {children}
    </div>
  )
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <div className="text-[12px] text-slate-400">{label}{hint && <span className="ml-1 text-slate-300">· {hint}</span>}</div>
      {children}
    </div>
  )
}

const Row2 = ({ children }) => <div className="grid grid-cols-2 gap-2">{children}</div>

function Line({ k, children }) {
  return (
    <div className="px-3 py-2.5 flex items-center justify-between gap-3">
      <span className="text-[12px] text-slate-400 shrink-0">{k}</span>
      <span className="text-right min-w-0">{children}</span>
    </div>
  )
}
